"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import type { Ingredient } from "@/lib/content/ingredients";

interface IngredientsLibraryProps {
  locale: string;
  ingredients: Ingredient[];
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function evidenceLabel(category: string): string {
  // Approximate evidence tag from category — keeps the design's "STRONG / MODERATE" feel
  if (/active|antiox|uv|peptide|retin/i.test(category)) return "STRONG";
  if (/humect|emoll|barrier|amino/i.test(category)) return "STRONG";
  if (/preserv|stabil|texture|thicken|solvent/i.test(category)) return "FUNCTIONAL";
  return "MODERATE";
}

export default function IngredientsLibrary({ locale, ingredients }: IngredientsLibraryProps) {
  const [letter, setLetter] = useState<string>("");
  const [query, setQuery] = useState("");

  const present = useMemo(() => {
    const set = new Set<string>();
    ingredients.forEach((i) => {
      const c = i.inci_name?.[0]?.toUpperCase();
      if (c) set.add(c);
    });
    return set;
  }, [ingredients]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ingredients.filter((i) => {
      const matchesLetter = !letter || i.inci_name.toUpperCase().startsWith(letter);
      const matchesQuery =
        !q ||
        i.inci_name.toLowerCase().includes(q) ||
        (i.commercial_name?.toLowerCase().includes(q) ?? false) ||
        i.benefits?.toLowerCase().includes(q) ||
        i.category?.toLowerCase().includes(q);
      return matchesLetter && matchesQuery;
    });
  }, [ingredients, letter, query]);

  return (
    <>
      {/* Sticky search + letter index */}
      <section
        style={{
          padding: "24px var(--bp-edge)",
          borderBottom: "1px solid var(--bp-rule)",
          background: "var(--bp-bg-2)",
          position: "sticky",
          top: 64,
          zIndex: 20,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 24,
            alignItems: "center",
          }}
          className="bp-ing-controls"
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by INCI name, common name, or benefit…"
            style={{
              padding: "12px 14px",
              border: "1px solid var(--bp-rule)",
              background: "var(--bp-bg)",
              fontFamily: "var(--bp-mono)",
              fontSize: 12,
              outline: "none",
              minWidth: 0,
              color: "var(--bp-ink)",
            }}
          />
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <button
              onClick={() => setLetter("")}
              className="bp-mono-sm"
              style={{
                padding: "6px 8px",
                color: !letter ? "var(--bp-ink)" : "var(--bp-ink-3)",
                background: "none",
                border: "1px solid",
                borderColor: !letter ? "var(--bp-ink)" : "transparent",
                cursor: "pointer",
              }}
            >
              ALL
            </button>
            {LETTERS.map((L) => {
              const has = present.has(L);
              const active = letter === L;
              return (
                <button
                  key={L}
                  onClick={() => has && setLetter(active ? "" : L)}
                  disabled={!has}
                  className="bp-mono-sm"
                  style={{
                    padding: "6px 6px",
                    minWidth: 24,
                    color: active ? "var(--bp-ink)" : has ? "var(--bp-ink-2)" : "var(--bp-ink-3)",
                    background: "none",
                    border: "1px solid",
                    borderColor: active ? "var(--bp-ink)" : "transparent",
                    cursor: has ? "pointer" : "not-allowed",
                    opacity: has ? 1 : 0.4,
                  }}
                >
                  {L}
                </button>
              );
            })}
          </div>
        </div>
        <div className="bp-mono-sm" style={{ marginTop: 12 }}>
          {filtered.length} OF {ingredients.length} INGREDIENTS
        </div>

        <style>{`
          @media (max-width: 700px) {
            .bp-ing-controls { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      {/* Results */}
      <section style={{ padding: "32px var(--bp-edge) 96px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "48px 1.5fr 1fr 1fr auto",
            gap: 24,
            padding: "12px 0",
            borderBottom: "1px solid var(--bp-ink)",
          }}
          className="bp-ing-headers"
        >
          {["", "INCI / COMMON", "FUNCTION", "MECHANISM", "USED IN"].map((h, i) => (
            <span key={i} className="bp-mono-sm" style={{ color: "var(--bp-ink)" }}>{h}</span>
          ))}
        </div>

        {filtered.length === 0 && (
          <p style={{ color: "var(--bp-ink-3)", fontSize: 14, padding: "32px 0" }}>
            No ingredients match the current filters.
          </p>
        )}

        {filtered.map((ing) => (
          <div
            key={ing.slug}
            style={{
              display: "grid",
              gridTemplateColumns: "48px 1.5fr 1fr 1fr auto",
              gap: 24,
              padding: "20px 0",
              borderBottom: "1px solid var(--bp-rule)",
              alignItems: "start",
            }}
            className="bp-ing-row"
          >
            <span className="bp-letter-badge">{ing.inci_name[0]?.toUpperCase()}</span>
            <div>
              <Link
                href={`/${locale}/ingredients/${ing.slug}`}
                style={{
                  fontFamily: "var(--bp-serif)",
                  fontSize: 20,
                  fontWeight: 300,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                  color: "var(--bp-ink)",
                  display: "block",
                }}
              >
                {ing.inci_name}
              </Link>
              {ing.commercial_name && (
                <div className="bp-mono-sm" style={{ marginTop: 4 }}>
                  {ing.commercial_name}
                </div>
              )}
            </div>
            <div style={{ fontSize: 13, color: "var(--bp-ink-2)" }}>{ing.primary_function || ing.cosing_function}</div>
            <div style={{ fontSize: 13, color: "var(--bp-ink-2)", lineHeight: 1.5 }}>
              {ing.mechanism || ing.benefits}
            </div>
            <div style={{ minWidth: 80, textAlign: "right" }}>
              <span className="bp-mono-sm">
                {ing.found_in_products.length > 0
                  ? `${ing.found_in_products.length} SKU${ing.found_in_products.length === 1 ? "" : "s"}`
                  : evidenceLabel(ing.category)}
              </span>
            </div>
          </div>
        ))}

        <style>{`
          @media (max-width: 900px) {
            .bp-ing-headers { display: none !important; }
            .bp-ing-row {
              grid-template-columns: 32px 1fr auto !important;
              gap: 12px !important;
            }
            .bp-ing-row > :nth-child(3),
            .bp-ing-row > :nth-child(4) { display: none; }
          }
        `}</style>
      </section>
    </>
  );
}
